import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  Input,
  OnDestroy,
  OnInit,
  QueryList,
  ViewChildren,
} from '@angular/core';
import {
  FormGroupDirective,
  UntypedFormArray,
  UntypedFormControl,
  UntypedFormGroup,
} from '@angular/forms';
import { Observable, Subject, combineLatest, throwError } from 'rxjs';
import { catchError, startWith, take, takeUntil } from 'rxjs/operators';
import { FormConfigService } from 'src/app/form-config/form-config.service';
import {
  PHYSICAL_SETTLEMENT_SENSITIVE_FIELDS,
  PhysicalSettlementMatrix,
} from 'src/app/form-config/mockups/consts/physical-settlement-matrix.const';
import {
  ReferenceEntity,
  ReferenceObligation,
} from 'src/app/static-data/models/interfaces/static-data';
import { ACCRUED_INTEREST } from '../../model/consts/accrued-interest.const';
import { APPLICABLE } from '../../model/consts/applicable.const';
import { CASH_SETTLEMENT_QUOTATION } from '../../model/consts/cash-settlement-quotation.const';
import { CASH_SETTLEMENT_VALUATION } from '../../model/consts/cash-settlement-valuation.const';
import {
  CREDIT_EVENTS,
  CreditEventId,
} from '../../model/consts/credit-events.const';
import { LOSS_AT_MATURITY } from '../../model/consts/loss-at-maturity.const';

import { isNullOrUndefined } from '@swimlane/ngx-datatable';
import {
  NO_CREDIT_PROVISIONS,
  PRODUCT_HIDDEN_SECTIONS,
} from 'src/app/form-config/model/product-settings/product-sections.settings';
import { DcToggleComponent } from 'src/app/ui-kit/dc-toggle/dc-toggle.component';
import { FALLBACK_SETTLEMENT_METHOD } from '../../model/consts/fallback-settlement.const';
import {
  ALL_SETTLEMENT_METHODS,
  RECOVERY_SETTLEMENT_METHODS,
} from '../../model/consts/settlement-method.const';
import { VALUATION_OBLIGATION_CATEGORIES } from '../../model/consts/valuation-obligation-categories';
import { VALUATION_OBLIGATION_CHARACTERISTICS } from '../../model/consts/valuation-obligation-characteristics';
import {
  CreditProvisionEnum,
  ProductCaptureGroupsEnum,
  SummaryEnum,
} from '../../model/enums/product-capture.enums';
import { ProductCaptureData } from '../../model/interfaces/product-capture.interface';
import { ProductCaptureService } from '../../services/product-capture.service';
import { CreateProductComponent } from '../create-product.component';
import { CreditProvisionService } from './credit-provision.service';
import { createSettlementProvisionFormGroup } from './credit-provision.utils';
import { SettlementMethodComponent } from './settlement-method/settlement-method.component';

interface ListItems {
  id: number;
  displayValue: string;
}

@Component({
  selector: 'app-credit-provision',
  templateUrl: './credit-provision.component.html',
  styleUrls: ['./credit-provision.component.scss'],
})
export class CreditProvisionComponent
  implements OnInit, OnDestroy, AfterViewInit
{
  @Input()
  formGroupName: string;

  @Input()
  formName: string;

  isLoadingPhysicalMatrixData: boolean;

  referenceEntitiesIds: Map<number, ReferenceEntity> = new Map();

  form: UntypedFormGroup;
  labels: Record<string, any>;
  currencyItems: Record<string, any>;
  applicableItems = APPLICABLE;
  lossAtMaturityItems = LOSS_AT_MATURITY;
  settlementItems = ALL_SETTLEMENT_METHODS;
  fallbackSettlementItems = FALLBACK_SETTLEMENT_METHOD;

  referenceObligationsData: ReferenceObligation[];
  referenceObligationItemsFiltered: ListItems[];

  creditPeriodStartDaysItems = [
    {
      id: '0',
      displayValue: '0',
    },
    {
      id: '60',
      displayValue: '60',
    },
  ];
  creditEventsItems = CREDIT_EVENTS;
  accruedInterestItems = ACCRUED_INTEREST;
  cashSettlementQuotationItems = CASH_SETTLEMENT_QUOTATION;
  cashSettlementValuationItems = CASH_SETTLEMENT_VALUATION;
  valuationObligationCategoriesItems = VALUATION_OBLIGATION_CATEGORIES;
  valuationObligationCharacteristicsItems =
    VALUATION_OBLIGATION_CHARACTERISTICS;
  existingReferenceEntities: Set<number> = new Set();

  private destroy$ = new Subject<void>();

  @ViewChildren(SettlementMethodComponent)
  entitiesSettings: QueryList<SettlementMethodComponent>;

  @ViewChildren(DcToggleComponent)
  toggles: QueryList<DcToggleComponent>;

  constructor(
    private formConfigService: FormConfigService,
    private rootFormGroup: FormGroupDirective,
    private productCaptureService: ProductCaptureService,
    private creditProvisionService: CreditProvisionService,
    private createProductComponent: CreateProductComponent,
    private cdr: ChangeDetectorRef
  ) {}

  ngOnInit(): void {
    this.form = this.rootFormGroup.control.get(
      this.formGroupName
    ) as UntypedFormGroup;
    this.labels = this.formConfigService.getLabels();
    this.getCurrencies();

    const summary = this.rootFormGroup.control.get(
      ProductCaptureGroupsEnum.summary
    ) as UntypedFormGroup;

    const referenceEntitiesDisplay = summary.get(
      SummaryEnum.referenceEntitiesDisplay
    ) as UntypedFormControl;

    referenceEntitiesDisplay.valueChanges.subscribe((data) => {
      this.referenceObligationItemsFiltered = this.filterReferenceObligations(
        data,
        this.referenceObligationsData
      );
    });

    this.subscribeToSettlementMethodForFixedRecovery();

    this.getReferenceObligations(referenceEntitiesDisplay);

    this.updateSettlementProvisionsOnReferenceEntitiesChanges();
    this.updateGracePeriodOnValueChanges();
  }

  ngAfterViewInit(): void {
    combineLatest([
      this.entitiesSettings.changes,
      this.physicalSettlementValueChanges,
    ])
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        this.applyPhysicalSettlementMatrixRules();
        this.cdr.detectChanges();
      });
  }

  private subscribeToSettlementMethodForFixedRecovery(): void {
    this.form
      .get(CreditProvisionEnum.settlementMethod)
      .valueChanges.pipe(
        takeUntil(this.destroy$),
        startWith(this.form.get(CreditProvisionEnum.settlementMethod).value)
      )
      .subscribe((settlementMethod: string) => {
        if (settlementMethod === 'FIXED_RECOVERY') {
          this.form.get(CreditProvisionEnum.fixedRecoveryAmount).enable();
        } else {
          this.form.get(CreditProvisionEnum.fixedRecoveryAmount).disable();
          this.form.get(CreditProvisionEnum.fixedRecoveryAmount).setValue(null);
        }
      });
  }

  private setSettlementMethodOptions(
    physicalSettlementMatrixApplicable: boolean,
    settlementMethod: string | null
  ): void {
    if (!physicalSettlementMatrixApplicable) {
      this.settlementItems = ALL_SETTLEMENT_METHODS;
      return;
    }

    if (settlementMethod) {
      this.settlementItems = [
        ...RECOVERY_SETTLEMENT_METHODS,
        ALL_SETTLEMENT_METHODS.find((method) => method.id === settlementMethod),
      ];

      return;
    }

    this.settlementItems = RECOVERY_SETTLEMENT_METHODS;
  }

  updateSettlementProvisionsOnReferenceEntitiesChanges(): void {
    this.creditProvisionService.selectedReferenceEntities$
      .pipe(takeUntil(this.destroy$))
      .subscribe((data: ReferenceEntity[]) => {
        this.referenceEntitiesIds.clear();

        data?.forEach((entity: ReferenceEntity) => {
          this.referenceEntitiesIds.set(entity.referenceEntityID, entity);
        });
        this.removeSettlementProvisions();
        this.createSettlementProvisions(data);
        this.sortSettlementProvisionsByRefEntity(data);
        this.cdr.detectChanges();
      });
  }

  sortSettlementProvisionsByRefEntity(data: ReferenceEntity[]): void {
    this.settlementProvisions.controls.sort((a, b) => {
      const referenceEntityIdA = a.get('referenceEntityId').value;
      const referenceEntityIdB = b.get('referenceEntityId').value;

      const indexA = data.findIndex(
        (entity) => entity.referenceEntityID === referenceEntityIdA
      );
      const indexB = data.findIndex(
        (entity) => entity.referenceEntityID === referenceEntityIdB
      );

      return indexA - indexB;
    });
  }

  updateGracePeriodOnValueChanges(): void {
    combineLatest([
      this.creditEvents.valueChanges.pipe(startWith(this.creditEvents.value)),
      this.physicalSettlementValueChanges,
    ])
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        const selections = this.creditEvents.value;
        const includes = selections?.includes(
          CreditEventId.GRACE_PERIOD_EXTENSION
        );
        const readOnly =
          includes && !this.physicalSettlementMatrixControl.value;
        this.creditEventGracePeriod['readOnly'] = !readOnly;

        const value = includes
          ? this.product.creditProvision.creditEventGracePeriod ?? '30D'
          : '';
        this.creditEventGracePeriod.setValue(value, { emitEvent: false });
      });
  }

  private getReferenceObligations(
    referenceEntitiesDisplay: UntypedFormControl
  ): void {
    this.productCaptureService
      .getReferenceObligations()
      .subscribe((data: ReferenceObligation[]) => {
        this.referenceObligationsData = data;

        const isCreditProvisionsHidden =
          PRODUCT_HIDDEN_SECTIONS.get(this.product?.summary?.productType) ===
          NO_CREDIT_PROVISIONS;

        referenceEntitiesDisplay.updateValueAndValidity({
          onlySelf: true,
          emitEvent: !isCreditProvisionsHidden,
        });
      });
  }

  private filterReferenceObligations(
    referenceEntities: number[],
    referenceObligationsData: ReferenceObligation[]
  ): ListItems[] {
    if (
      !referenceEntities ||
      !referenceObligationsData ||
      referenceEntities.length === 0 ||
      referenceObligationsData.length === 0
    ) {
      return [];
    }
    const filteredReferences = referenceEntities.map((n) => {
      return referenceObligationsData.filter(
        (obj) => obj.referenceEntityID === n
      );
    });
    const filteredReferencesFlat = filteredReferences.flat();
    const filteredReferenceItems = filteredReferencesFlat.map((ref) => {
      return {
        id: ref.referenceObligationID,
        displayValue: ref.primaryObligor,
      };
    });
    return filteredReferenceItems;
  }

  applyPhysicalSettlementMatrixRules(): void {
    const { value: applicable } = this.physicalSettlementMatrixControl;

    PHYSICAL_SETTLEMENT_SENSITIVE_FIELDS.forEach((name: string) => {
      this.form.get(name)['readOnly'] = applicable;
    });

    let component: SettlementMethodComponent;
    if ((component = this.entitiesSettings?.first)) {
      Object.keys(component.form.controls).forEach((key: string) => {
        component.form.get(key)['readOnly'] = applicable;
      });

      this.updateDataWithFirstMatrixDataIfApplicable();
    }
  }

  removeSettlementProvisions(): void {
    this.settlementProvisions.controls.forEach(
      (settlement: UntypedFormGroup, index: number) => {
        if (
          !this.referenceEntitiesIds.has(
            settlement.get('referenceEntityId').value
          )
        ) {
          this.settlementProvisions.removeAt(index);
          this.existingReferenceEntities.delete(
            settlement.get('referenceEntityId').value
          );
        }
      }
    );
  }

  createSettlementProvisions(referenceEntities: ReferenceEntity[]): void {
    referenceEntities
      .filter(
        (entity: ReferenceEntity) =>
          !this.settlementProvisions.controls.some(
            (settlement: UntypedFormGroup) =>
              settlement.get('referenceEntityId').value ===
              entity.referenceEntityID
          )
      )
      .forEach((entity: ReferenceEntity) => {
        this.settlementProvisions.push(
          createSettlementProvisionFormGroup(
            entity,
            this.product,
            this.form.value
          )
        );
      });
  }

  updateDataWithFirstMatrixDataIfApplicable(): void {
    this.isLoadingPhysicalMatrixData = !!this.entitiesSettings?.length;

    this.entitiesSettings?.first
      ?.fetchAndUpdateValues()
      .pipe(
        take(1),
        catchError((error) => {
          this.isLoadingPhysicalMatrixData = false;
          return throwError(error);
        })
      )
      .subscribe((matrix: PhysicalSettlementMatrix) => {
        this.isLoadingPhysicalMatrixData = false;

        const physicalSettlementMatrix = this.form.get(
          CreditProvisionEnum.physicalSettlementMatrix
        ).value;
        this.setSettlementMethodOptions(
          physicalSettlementMatrix,
          matrix?.settlementMethod
        );

        if (!matrix) {
          return;
        }

        const value: any = {};

        if (!this.isSettlementMethodUpdatable()) {
          return;
        }

        this.setFallbackSettlementMethodEditable();

        Object.keys(matrix).forEach((key: string) => {
          if (key === CreditProvisionEnum.fallbackSettlementMethod) {
            matrix[key] = 'CASH';
          }
          value[key] = matrix[key];
        });

        setTimeout(() => {
          this.form.patchValue(value);
        });
      });
  }

  setFallbackSettlementMethodEditable(): void {
    this.fallbackSettlementMethod['readOnly'] = false;
  }

  get fallbackSettlementMethod(): UntypedFormArray {
    return this.form.get(
      CreditProvisionEnum.fallbackSettlementMethod
    ) as UntypedFormArray;
  }

  private getCurrencies(): void {
    this.productCaptureService.getCurrencies().subscribe((currencies) => {
      if (currencies?.length > 0 && !this.currencyItems?.length) {
        this.currencyItems = currencies.map((currency) => {
          return {
            id: currency.shortName,
            displayValue: currency.shortName,
          };
        });
      }
    });
  }

  isSettlementMethodUpdatable(): boolean {
    return isNullOrUndefined(this.product.id);
  }

  get physicalSettlementValueChanges(): Observable<any> {
    return this.physicalSettlementMatrixControl.valueChanges.pipe(
      startWith(this.physicalSettlementMatrixControl.value)
    );
  }

  get settlementProvisions(): UntypedFormArray {
    return this.form.get(
      CreditProvisionEnum.settlementProvisions
    ) as UntypedFormArray;
  }

  get creditEventGracePeriod(): UntypedFormControl {
    return this.form.get(
      CreditProvisionEnum.creditEventGracePeriod
    ) as UntypedFormControl;
  }

  get physicalSettlementMatrixControl(): UntypedFormControl {
    return this.form.get(
      CreditProvisionEnum.physicalSettlementMatrix
    ) as UntypedFormControl;
  }

  get creditEvents(): UntypedFormControl {
    return this.form.get(
      CreditProvisionEnum.creditEvents
    ) as UntypedFormControl;
  }

  get product(): ProductCaptureData {
    return this.createProductComponent.prod;
  }

  ngOnDestroy(): void {
    this.settlementProvisions.clear();
    this.destroy$.next();
    this.destroy$.unsubscribe();
  }
}
